/**
 * Pure helpers over the flat folder list the API returns (each row carries
 * only its `parentId`). Kept out of the media explorer so they're testable.
 */

export interface FolderLike {
  id: string;
  parentId: string | null;
  name: string;
}

/** Children of each parent id (`null` = top level), each list sorted by name. */
export function childrenByParent<F extends FolderLike>(folders: readonly F[]): Map<string | null, F[]> {
  const byParent = new Map<string | null, F[]>();
  for (const f of folders) {
    const list = byParent.get(f.parentId) ?? [];
    list.push(f);
    byParent.set(f.parentId, list);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  return byParent;
}

/** Root-to-folder chain for breadcrumbs; empty if `id` is unknown. */
export function ancestry<F extends FolderLike>(folders: readonly F[], id: string): F[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain: F[] = [];
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parentId !== null ? byId.get(cur.parentId) : undefined;
  }
  return chain;
}

/** True if `id` is `ancestorId` or lies anywhere beneath it. */
export function isWithin(folders: readonly FolderLike[], id: string, ancestorId: string): boolean {
  return ancestry(folders, id).some((f) => f.id === ancestorId);
}
